"use client";

import { useState, useEffect, useMemo, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Plus, ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { logger } from '@/lib/telemetry'

type JsonSchemaType = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'null' | 'integer';

interface SchemaProperty {
   id: string;
   name: string;
   type: JsonSchemaType;
   required: boolean;
   isExpanded?: boolean;
   description?: string;
   properties?: Record<string, SchemaProperty>;
   items?: SchemaProperty;
}

export interface SchemaEditorSchema {
   id?: string;
   name?: string;
   description?: string | null;
   schema_definition: Record<string, unknown>;
}

export interface SchemaEditorProps {
   schema: SchemaEditorSchema;
   onSchemaChange?: (schemaDefinition: Record<string, unknown>) => void;
   onSave?: (schema: SchemaEditorSchema) => void | Promise<void>;
   readOnly?: boolean;
}

const LOG_PREFIX = 'schema-editor.SchemaEditor';

const DATA_TYPES = [
   'string',
   'number',
   'boolean',
   'object',
   'array',
   'null',
   'integer'
]

function PropertyEditor({
   property,
   propertyName,
   onUpdate,
   onRemove,
   depth = 0,
   readOnly = false,
}: {
   property: SchemaProperty
   propertyName: string
   onUpdate: (updates: Partial<SchemaProperty> & { name?: string }) => void
   onRemove: () => void
   depth?: number
   readOnly?: boolean
}) {
   const addNestedProperty = () => {
      const newPropName = `property_${Object.keys(property.properties || {}).length + 1}`;
      const newProperties = {
         ...property.properties,
         [newPropName]: {
            id: generateId(),
            name: newPropName,
            type: 'string' as JsonSchemaType,
            required: false
         }
      }
      onUpdate({ properties: newProperties })
   }

   const addArrayItem = () => {
      onUpdate({
         items: {
            id: generateId(),
            name: 'item',
            type: 'string' as JsonSchemaType,
            required: true
         }
      })
   }

   const updateNestedProperty = (propertyName: string, updates: Partial<SchemaProperty>) => {
      const newProperties = { ...property.properties }
      
      // If the name is being updated, we need to rename the key in the nested properties object
      if (updates.name && updates.name !== propertyName) {
         const existingProperty = property.properties?.[propertyName];
         const updatedProperty = {
            ...existingProperty,
            ...updates,
            id: existingProperty?.id || generateId(), // Preserve existing ID or generate new one
            name: updates.name,
            type: updates.type || existingProperty?.type || 'string',
            required: updates.required ?? existingProperty?.required ?? false
         }
         
         // Remove the old key and add with the new key
         delete newProperties[propertyName]
         newProperties[updates.name] = updatedProperty
      } else {
         // Just update the property without changing the key
         const existingProperty = property.properties?.[propertyName];
         newProperties[propertyName] = {
            ...existingProperty,
            ...updates,
            id: existingProperty?.id || generateId(), // Preserve existing ID or generate new one
            name: updates.name || existingProperty?.name || propertyName,
            type: updates.type || existingProperty?.type || 'string',
            required: updates.required ?? existingProperty?.required ?? false
         }
      }
      
      onUpdate({ properties: newProperties })
   }

   const removeNestedProperty = (propertyName: string) => {
      const newProperties = { ...property.properties }
      delete newProperties[propertyName]
      onUpdate({ properties: newProperties })
   }

   return (
      <div className="space-y-1">
         <div className="flex items-center gap-4 p-4" style={{ marginLeft: `${depth * 1.5}rem` }}>
            <Input
               className="flex-1"
               placeholder="Property name"
               value={property.name}
               disabled={readOnly}
               onChange={(e) => onUpdate({
                  ...property,
                  name: e.target.value
               })}
            />

            <Select
               value={property.type}
               disabled={readOnly}
               onValueChange={(value) => {
                  const updates: Partial<SchemaProperty> = { 
                     type: value as JsonSchemaType,
                     properties: value === 'object' ? {} : undefined,
                     isExpanded: value === 'object' || value === 'array' ? true : property.isExpanded
                  }
                  onUpdate(updates)
               }}
            >
               <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select type" />
               </SelectTrigger>
               <SelectContent>
                  {DATA_TYPES.map((type) => (
                     <SelectItem key={type} value={type}>
                        {type}
                     </SelectItem>
                  ))}
               </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
               <input
                  type="checkbox"
                  checked={property.required}
                  disabled={readOnly}
                  onChange={(e) => onUpdate({ required: e.target.checked })}
                  className="rounded border-gray-300"
               />
               <span className="text-sm">Required</span>
            </div>

            <Button
               variant="ghost"
               size="sm"
               type="button"
               onClick={() => onUpdate({ isExpanded: !property.isExpanded })}
               disabled={readOnly}
               className={property.type !== 'object' && property.type !== 'array' ? 'invisible' : ''}
            >
               {property.isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>

            <Button variant="ghost" className="w-8 h-8 hover:text-white  hover:bg-red-700 rounded-full" onClick={onRemove} disabled={readOnly} >
               <Trash2 className="min-w-4 cursor-pointer" />
            </Button>

         </div>

         <div className="pl-4 mb-10" style={{ marginLeft: `${depth * 1.5}rem` }}>
            <Input
               className="w-full"
               placeholder="Description"
               value={property.description || ''}
               disabled={readOnly}
               onChange={(e) => onUpdate({ description: e.target.value })}
            />
         </div>

         <div className="pl-4 mb-10" style={{ marginLeft: `${depth * 1.5}rem` }}>
            <Separator className="my-2 pt-1" />
         </div>

         {property.isExpanded && (
            <div className="pl-8">
               {property.type === 'object' && (
                  <>
                     {Object.entries(property.properties || {}).map(([childName, childProp]) => (
                        <PropertyEditor
                           key={childProp.id || childName}
                           property={childProp}
                           propertyName={childName}
                           onUpdate={(updates) => updateNestedProperty(childName, updates)}
                           onRemove={() => removeNestedProperty(childName)}
                           depth={depth + 1}
                           readOnly={readOnly}
                        />
                     ))}
                     <Button
                        size="sm"
                        type="button"
                        onClick={addNestedProperty}
                        disabled={readOnly}
                        className="ml-auto mr-4 mt-2"
                     >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Property
                     </Button>
                  </>
               )}
               {property.type === 'array' && !property.items && (
                  <Button
                     size="sm"
                     type="button"
                     onClick={addArrayItem}
                     disabled={readOnly}
                     className="ml-4"
                  >
                     <Plus className="h-4 w-4 mr-2" />
                     Add Array Item
                  </Button>
               )}
               {property.type === 'array' && property.items && (
                  <PropertyEditor
                     property={property.items}
                     propertyName="items"
                     onUpdate={(updates) => onUpdate({ 
                        items: { 
                           ...property.items!, 
                           ...updates,
                           name: updates.name || property.items!.name
                        } 
                     })}
                     onRemove={() => onUpdate({ items: undefined })}
                     depth={depth + 1}
                     readOnly={readOnly}
                  />
               )}
            </div>
         )}
      </div>
   )
}

function generateId(): string {
   return Math.random().toString(36).substr(2, 9);
}

function parseJsonSchema(jsonSchema: string | Record<string, unknown>): Record<string, SchemaProperty> {
   try {
      let schema;
      if (typeof jsonSchema === 'string') {
         if (!jsonSchema) {
            logger.info(`${LOG_PREFIX}.parseJsonSchema: empty schema definition`);
            return {};
         }
         schema = JSON.parse(jsonSchema);
      } else {
         schema = jsonSchema;
      }
      
      if (!schema || !schema.properties) {
         logger.info(`${LOG_PREFIX}.parseJsonSchema: no top-level properties`);
         return {};
      }

      const properties: Record<string, SchemaProperty> = {};

      Object.entries(schema.properties || {}).forEach(([key, value]: [string, any]) => {
         const property: SchemaProperty = {
            id: generateId(),
            name: key,
            type: value.type,
            required: (schema.required || []).includes(key),
            isExpanded: true,
            description: value.description || ""
         }

         if (value.type === 'object') {
            property.properties = {}
            if (value.properties) {
               Object.entries(value.properties).forEach(([childKey, childValue]: [string, any]) => {
                  property.properties![childKey] = {
                     id: generateId(),
                     name: childKey,
                     type: childValue.type,
                     required: (value.required || []).includes(childKey),
                     isExpanded: true,
                     description: childValue.description || "",
                     ...(childValue.type === 'object' && { properties: {} })
                  }
               })
            }
         }

         if (value.type === 'array' && value.items) {
            property.items = {
               id: generateId(),
               name: 'items',
               type: value.items.type,
               required: true,
               isExpanded: true,
               description: value.items.description || "",
               ...(value.items.type === 'object' && {
                  properties: Object.entries(value.items.properties || {}).reduce((acc, [itemKey, itemValue]: [string, any]) => ({
                     ...acc,
                     [itemKey]: {
                        id: generateId(),
                        name: itemKey,
                        type: itemValue.type,
                        required: (value.items.required || []).includes(itemKey),
                        isExpanded: true,
                        description: itemValue.description || ""
                     }
                  }), {})
               })
            }
         }

         properties[key] = property
      })

      return properties
   } catch (e) {
      logger.error(`${LOG_PREFIX}.parseJsonSchema: failed to parse schema`, { error: e });
      return {};
   }
}

function convertToJsonSchema(properties: Record<string, SchemaProperty>): Record<string, unknown> {
   const convertProperty = (prop: SchemaProperty): any => {
      const result: any = {
         type: prop.type,
         description: prop.description || undefined
      }
      
      // Don't include the id field in the JSON schema output

      if (prop.type === 'object' && prop.properties) {
         result.properties = {}
         result.required = []
         
         Object.entries(prop.properties).forEach(([key, childProp]) => {
            result.properties[key] = convertProperty(childProp)
            if (childProp.required) {
               result.required.push(key)
            }
         })

         if (result.required.length === 0) {
            delete result.required
         }
      }

      if (prop.type === 'array' && prop.items) {
         result.items = convertProperty(prop.items)
      }

      return result
   }

   const schema: {
      $schema: string;
      type: 'object';
      properties: Record<string, unknown>;
      required?: string[];
   } = {
      "$schema": "http://json-schema.org/draft-07/schema#",
      "type": "object",
      "properties": {},
      "required": [] as string[]
   }

   const required: string[] = [];
   Object.entries(properties).forEach(([key, prop]) => {
      schema.properties[key] = convertProperty(prop)
      if (prop.required) {
         required.push(key)
      }
   })

   schema.required = required.length > 0 ? required : undefined;

   return schema
}

export function SchemaEditor({ schema, onSchemaChange, onSave, readOnly = false }: SchemaEditorProps) {
   const [properties, setProperties] = useState<Record<string, SchemaProperty>>(() => {
      return parseJsonSchema(schema.schema_definition);
   });

   const [jsonSchema, setJsonSchema] = useState<string>(() => {
      return JSON.stringify(schema.schema_definition, null, 2);
   });

   const onSchemaChangeRef = useRef(onSchemaChange);
   onSchemaChangeRef.current = onSchemaChange;
   const jsonSchemaRef = useRef(jsonSchema);
   jsonSchemaRef.current = jsonSchema;
   // Skip notifying parent for the initial parse and for prop→state syncs.
   const suppressNotifyRef = useRef(true);

   const incomingSchemaSerialized = useMemo(
      () => JSON.stringify(schema.schema_definition ?? {}, null, 2),
      [schema.schema_definition]
   );

   // Push local property edits to JSON + parent (user-driven changes only).
   useEffect(() => {
      if (suppressNotifyRef.current) {
         suppressNotifyRef.current = false;
         return;
      }
      const newSchema = convertToJsonSchema(properties);
      const newJsonSchema = JSON.stringify(newSchema, null, 2);
      setJsonSchema(newJsonSchema);
      onSchemaChangeRef.current?.(newSchema);
   }, [properties]);

   // Sync from parent when the incoming definition actually differs from local JSON.
   useEffect(() => {
      if (incomingSchemaSerialized === jsonSchemaRef.current) {
         return;
      }
      suppressNotifyRef.current = true;
      setProperties(parseJsonSchema(schema.schema_definition));
      setJsonSchema(incomingSchemaSerialized);
   }, [incomingSchemaSerialized, schema.schema_definition]);

   // Update properties when JSON changes (for two-way sync)
   const handleJsonChange = (newJson: string) => {
      setJsonSchema(newJson);
      try {
         const parsed = JSON.parse(newJson);
         const newProperties = parseJsonSchema(parsed);
      if (!readOnly) {
         setProperties(newProperties);
      }
      } catch (_e) {
         // Invalid JSON, don't update properties
         logger.info(`${LOG_PREFIX}.handleJsonChange: skipped invalid json`);
      }
   };

   const updateProperty = (
      propertyName: string,
      updates: Partial<SchemaProperty>
   ) => {
      const newProperties = { ...properties }
      
      // If the name is being updated, we need to rename the key in the properties object
      if (updates.name && updates.name !== propertyName) {
         const existingProperty = properties[propertyName];
         const updatedProperty = {
            ...existingProperty,
            ...updates,
            id: existingProperty?.id || generateId(), // Preserve existing ID or generate new one
         } as SchemaProperty
         
         // Remove the old key and add with the new key
         delete newProperties[propertyName]
         newProperties[updates.name] = updatedProperty
      } else {
         // Just update the property without changing the key
         const existingProperty = properties[propertyName];
         newProperties[propertyName] = {
            ...existingProperty,
            ...updates,
            id: existingProperty?.id || generateId(), // Preserve existing ID or generate new one
         } as SchemaProperty
      }
      
      if (!readOnly) {
         setProperties(newProperties)
      }
   }

   const addProperty = () => {
      const newPropName = `property_${Object.keys(properties).length + 1}`;
      const newProp: SchemaProperty = {
         id: generateId(),
         name: newPropName,
         type: 'string',
         required: false,
      }
      updateProperty(newPropName, newProp)
   }

   const removeProperty = (propertyName: string) => {
      const newProperties = { ...properties }
      delete newProperties[propertyName]
      if (!readOnly) {
         setProperties(newProperties)
      }
   }

   const handleSave = () => {
      const updatedSchema = {
         ...schema,
         schema_definition: convertToJsonSchema(properties)
      };
      onSave?.(updatedSchema);
   };

   return (
      <div className="h-full flex flex-col">
         {/* Header */}
         <div className="shrink-0 p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between">
               <div>
                  <h3 className="text-lg font-semibold">Schema Editor: {schema.name}</h3>
                  <p className="text-sm text-gray-600">{schema.description}</p>
               </div>
               <Button onClick={handleSave} disabled={readOnly}>
                  Save Schema
               </Button>
            </div>
         </div>

         {/* Two-panel layout */}
         <div className="flex-1 flex min-h-0">
            {/* Left Panel - Visual Editor */}
            <div className="w-1/2 border-r flex flex-col min-h-0">
               <div className="shrink-0 p-4 border-b bg-gray-50">
                  <h4 className="text-md font-semibold">Visual Editor</h4>
                  <p className="text-sm text-gray-600">
                     Add and configure schema properties
                  </p>
               </div>
               <div className="flex-1 overflow-auto p-4">
                  <div className="space-y-2">
                     {Object.entries(properties).map(([propName, property]) => (
                        <PropertyEditor
                           key={property.id || propName}
                           property={property}
                           propertyName={propName}
                           onUpdate={(updates) => updateProperty(propName, updates)}
                           onRemove={() => removeProperty(propName)}
                           depth={0}
                           readOnly={readOnly}
                        />
                     ))}
                  </div>

                  <div className="flex mt-6">
                     <Button onClick={addProperty} size="sm" type="button" disabled={readOnly}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Property
                     </Button>
                  </div>
               </div>
            </div>

            {/* Right Panel - JSON Schema Text Editor */}
            <div className="w-1/2 flex flex-col min-h-0">
               <div className="shrink-0 p-4 border-b bg-gray-50">
                  <h4 className="text-md font-semibold">JSON Schema</h4>
                  <p className="text-sm text-gray-600">
                     View and edit the raw JSON Schema
                  </p>
               </div>
               <div className="flex-1 min-h-0">
                  <Textarea
                     value={jsonSchema}
                     onChange={(event) => handleJsonChange(event.target.value)}
                     readOnly={readOnly}
                     className="h-full min-h-full resize-none rounded-none border-0 font-mono text-xs"
                  />
               </div>
            </div>
         </div>
      </div>
   )
} 